import React, { useState } from "react";
import { useBlockchainInfo, useSweepDeployer, useTransferOwnership, useTreasuryPayout, useAdminEscrows, useReleaseEscrow, useRefundEscrow, useResolveEscrow, type EscrowRow } from "@/hooks/use-queries";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, Wallet, Landmark, ArrowRightLeft, DollarSign, AlertTriangle, SendHorizonal, Lock, Unlock, RotateCcw, Scale } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

export default function Blockchain() {
  const { data, isLoading, isFetching, error, dataUpdatedAt } = useBlockchainInfo();
  const queryClient = useQueryClient();
  const sweepDeployer = useSweepDeployer();
  const transferOwnership = useTransferOwnership();

  const [newOwnerInput, setNewOwnerInput] = useState("");

  const escrows = useAdminEscrows();
  const releaseEscrow = useReleaseEscrow();
  const refundEscrow = useRefundEscrow();
  const resolveEscrow = useResolveEscrow();
  const [resolveState, setResolveState] = useState<{
    jobId: string;
    posterBps: string;
    agentBps: string;
  } | null>(null);

  // Treasury Payout state
  const treasuryPayout = useTreasuryPayout();
  const [payoutToken, setPayoutToken] = useState<'ETH' | 'USDC'>('ETH');
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutTo, setPayoutTo] = useState("");
  const [payoutConfirming, setPayoutConfirming] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-blockchain-info"] });

  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : null;

  const handleSweep = async () => {
    try {
      const result = await sweepDeployer.mutateAsync();
      toast.success(`Swept ${result.amountEth} ETH → ${result.txHash.slice(0, 10)}…`, {
        action: result.txHash
          ? {
              label: "View",
              onClick: () => {
                const base = data?.chain?.explorerUrl?.replace(/\/address\/.*/, "") ?? "https://basescan.org";
                window.open(`${base}/tx/${result.txHash}`, "_blank");
              },
            }
          : undefined,
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Sweep failed");
    }
  };

  const handleTransferOwnership = async () => {
    try {
      const result = await transferOwnership.mutateAsync(newOwnerInput);
      toast.success(`Ownership transferred to ${result.newOwner.slice(0, 10)}…`);
      setNewOwnerInput("");
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Transfer failed");
    }
  };

  const handleTreasuryPayout = async () => {
    try {
      const result = await treasuryPayout.mutateAsync({ to: payoutTo, amount: payoutAmount, token: payoutToken });
      toast.success(`Sent ${result.amount} ${result.token} → ${result.to.slice(0, 10)}…`, {
        action: result.txHash
          ? {
              label: "Basescan",
              onClick: () => window.open(`${explorerBase}/tx/${result.txHash}`, "_blank"),
            }
          : undefined,
      });
      setPayoutAmount("");
      setPayoutTo("");
      setPayoutConfirming(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Payout failed");
      setPayoutConfirming(false);
    }
  };

  const explorerBase = data?.chain?.explorerUrl?.replace(/\/address\/.*/, "") ?? "https://basescan.org";

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-mono text-primary uppercase tracking-[0.2em] mb-1">Admin</h2>
          <h1 className="text-3xl font-display font-bold">Blockchain Controls</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Relay on-chain operations server-side — no MetaMask required.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-[10px] font-mono text-muted-foreground">Updated {lastUpdated}</span>
          )}
          <Button variant="outline" size="sm" onClick={refresh} disabled={isFetching}>
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-4 flex items-center gap-3 text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <p className="text-sm">Failed to load blockchain info — check API and RPC config.</p>
          </CardContent>
        </Card>
      )}

      {/* ── Escrow Operations ─────────────────────────────────────── */}
      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-muted-foreground">
            <Lock className="h-4 w-4 text-cyan-400" />
            Escrow Operations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              All jobs with on-chain USDC escrow — release to agent, refund to poster, or resolve disputes.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-blockchain-escrows'] })}
              disabled={escrows.isFetching}
              className="gap-2 shrink-0"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${escrows.isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {escrows.isLoading && (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full bg-muted" />)}
            </div>
          )}

          {escrows.data && escrows.data.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-border/30">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border/30">
                    <TableHead className="font-mono text-[10px] uppercase text-muted-foreground">Job</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-muted-foreground">Status</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-muted-foreground">USDC</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-muted-foreground">On-Chain</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {escrows.data.map((row: EscrowRow) => {
                    const oc = row.onChain;
                    const isDone = oc?.released || oc?.refunded || oc?.disputeResolved;
                    return (
                      <React.Fragment key={row.jobId}>
                        <TableRow className="border-border/20 hover:bg-surface-subtle/60">
                          <TableCell className="py-2">
                            <p className="text-xs font-medium truncate max-w-[200px]">{row.title}</p>
                            <p className="font-mono text-[9px] text-muted-foreground">{row.jobId.slice(0, 8)}…</p>
                          </TableCell>
                          <TableCell className="py-2">
                            <Badge variant="outline" className="text-[9px] font-mono uppercase">
                              {row.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-2">
                            <span className="font-mono text-sm">${oc?.amountUsdc ?? row.budgetUsdc}</span>
                          </TableCell>
                          <TableCell className="py-2">
                            {!oc ? (
                              <span className="text-[10px] text-muted-foreground font-mono">RPC err</span>
                            ) : isDone ? (
                              <Badge variant="outline" className="text-[9px] border-emerald-500/50 text-emerald-600 font-mono uppercase">
                                {oc.released ? 'Released' : oc.refunded ? 'Refunded' : 'Resolved'}
                              </Badge>
                            ) : oc.disputed ? (
                              <Badge variant="outline" className="text-[9px] border-orange-500/50 text-orange-600 font-mono uppercase">Disputed</Badge>
                            ) : oc.agentAssigned ? (
                              <Badge variant="outline" className="text-[9px] border-blue-500/50 text-blue-600 font-mono uppercase">Assigned</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[9px] border-primary/50 text-primary font-mono uppercase">Funded</Badge>
                            )}
                          </TableCell>
                          <TableCell className="py-2">
                            {!isDone && (
                              <div className="flex items-center gap-1">
                                {/* Release */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={!oc?.agentAssigned || releaseEscrow.isPending}
                                  title="Release to agent"
                                  onClick={async () => {
                                    try {
                                      await releaseEscrow.mutateAsync(row.jobId);
                                      toast.success(`Released escrow for ${row.title.slice(0, 30)}…`);
                                    } catch (err: any) {
                                      toast.error(err?.response?.data?.message ?? 'Release failed');
                                    }
                                  }}
                                  className="h-7 w-7 p-0"
                                >
                                  <Unlock className="h-3.5 w-3.5 text-emerald-400" />
                                </Button>
                                {/* Refund */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={refundEscrow.isPending}
                                  title="Refund to poster"
                                  onClick={async () => {
                                    try {
                                      await refundEscrow.mutateAsync(row.jobId);
                                      toast.success(`Refunded escrow for ${row.title.slice(0, 30)}…`);
                                    } catch (err: any) {
                                      toast.error(err?.response?.data?.message ?? 'Refund failed');
                                    }
                                  }}
                                  className="h-7 w-7 p-0"
                                >
                                  <RotateCcw className="h-3.5 w-3.5 text-amber-400" />
                                </Button>
                                {/* Resolve Dispute */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={resolveEscrow.isPending}
                                  title="Resolve dispute"
                                  onClick={() => setResolveState({ jobId: row.jobId, posterBps: '5000', agentBps: '5000' })}
                                  className="h-7 w-7 p-0"
                                >
                                  <Scale className="h-3.5 w-3.5 text-purple-400" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Inline resolve form */}
                        {resolveState?.jobId === row.jobId && (
                          <TableRow className="border-border/20 bg-purple-500/5">
                            <TableCell colSpan={5} className="py-3">
                              <div className="space-y-3">
                                <p className="text-xs font-mono font-bold text-purple-600 uppercase">Resolve Dispute — bps (must sum to 10000)</p>
                                <div className="flex items-center gap-3 flex-wrap">
                                  <div className="flex items-center gap-2">
                                    <label className="text-[10px] font-mono text-muted-foreground uppercase">Poster bps</label>
                                    <Input
                                      type="number"
                                      value={resolveState.posterBps}
                                      onChange={(e) => setResolveState(s => s ? { ...s, posterBps: e.target.value } : null)}
                                      className="w-24 font-mono text-sm h-8"
                                      min="0"
                                      max="10000"
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <label className="text-[10px] font-mono text-muted-foreground uppercase">Agent bps</label>
                                    <Input
                                      type="number"
                                      value={resolveState.agentBps}
                                      onChange={(e) => setResolveState(s => s ? { ...s, agentBps: e.target.value } : null)}
                                      className="w-24 font-mono text-sm h-8"
                                      min="0"
                                      max="10000"
                                    />
                                  </div>
                                  <span className={`text-[10px] font-mono ${
                                    (Number(resolveState.posterBps) + Number(resolveState.agentBps)) === 10000
                                      ? 'text-emerald-600'
                                      : 'text-destructive'
                                  }`}>
                                    Sum: {Number(resolveState.posterBps) + Number(resolveState.agentBps)} / 10000
                                  </span>
                                  <Button
                                    size="sm"
                                    disabled={
                                      resolveEscrow.isPending ||
                                      (Number(resolveState.posterBps) + Number(resolveState.agentBps)) !== 10000
                                    }
                                    onClick={async () => {
                                      try {
                                        await resolveEscrow.mutateAsync({
                                          jobId: row.jobId,
                                          posterBps: Number(resolveState.posterBps),
                                          agentBps: Number(resolveState.agentBps),
                                        });
                                        toast.success(`Dispute resolved for ${row.title.slice(0, 30)}…`);
                                        setResolveState(null);
                                      } catch (err: any) {
                                        toast.error(err?.response?.data?.message ?? 'Resolve failed');
                                      }
                                    }}
                                    className="gap-2 h-8"
                                  >
                                    {resolveEscrow.isPending ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Scale className="h-3 w-3" />}
                                    Confirm Resolve
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setResolveState(null)}
                                    className="h-8 text-muted-foreground"
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {escrows.data?.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No funded escrows found.</p>
          )}
        </CardContent>
      </Card>

      {/* Section 1: Deployer Wallet */}
      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-muted-foreground">
            <Wallet className="h-4 w-4 text-amber-400" />
            Deployer Wallet
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Deployer Address */}
          <div>
            <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Address</p>
            {isLoading && !data ? (
              <Skeleton className="h-5 w-64 bg-muted" />
            ) : data?.deployer?.address ? (
              <a
                href={`${explorerBase}/address/${data.deployer.address}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 font-mono text-sm text-primary/80 hover:text-primary"
              >
                {data.deployer.address.slice(0, 10)}...{data.deployer.address.slice(-8)}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <span className="font-mono text-sm text-muted-foreground">—</span>
            )}
          </div>

          {/* ETH Balance */}
          <div>
            <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">ETH Balance</p>
            {isLoading && !data ? (
              <Skeleton className="h-10 w-40 bg-muted" />
            ) : (
              <p className="text-3xl font-display font-bold">
                {data?.deployer?.ethBalance != null
                  ? `${parseFloat(data.deployer.ethBalance).toFixed(6)} ETH`
                  : "—"}
              </p>
            )}
          </div>

          {/* Sweep Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleSweep}
            disabled={sweepDeployer.isPending}
            className="gap-2"
          >
            {sweepDeployer.isPending ? (
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ArrowRightLeft className="h-3.5 w-3.5" />
            )}
            {sweepDeployer.isPending ? "Sweeping…" : "Sweep to Treasury"}
          </Button>
        </CardContent>
      </Card>

      {/* Section 2: Platform Fees */}
      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-muted-foreground">
            <DollarSign className="h-4 w-4 text-purple-400" />
            Platform Fees
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Check the{" "}
            <a href="/admin/treasury" className="text-primary underline">
              Treasury page
            </a>{" "}
            for the recorded fee amount. Withdrawals are disabled on MoltEscrowV2 because its pool can include funds
            required for active-job refunds. Re-enable this action only after migration to a fee-safe contract.
          </p>

          <Button
            variant="outline"
            size="sm"
            disabled
            className="gap-2"
          >
            <Lock className="h-3.5 w-3.5" />
            Fee Withdrawals Disabled
          </Button>
        </CardContent>
      </Card>

      {/* Section 3: Treasury Payout */}
      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-muted-foreground">
            <SendHorizonal className="h-4 w-4 text-blue-400" />
            Treasury Payout
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Send ETH or USDC from the treasury wallet to any address — no MetaMask required.
          </p>

          {/* Token selector */}
          <div className="flex gap-2">
            <Button
              variant={payoutToken === 'ETH' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPayoutToken('ETH')}
              disabled={treasuryPayout.isPending}
            >
              ETH
            </Button>
            <Button
              variant={payoutToken === 'USDC' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPayoutToken('USDC')}
              disabled={treasuryPayout.isPending}
            >
              USDC
            </Button>
          </div>

          {/* Inputs */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="number"
              placeholder="0.00"
              value={payoutAmount}
              onChange={(e) => { setPayoutAmount(e.target.value); setPayoutConfirming(false); }}
              className="font-mono text-sm w-36"
              disabled={treasuryPayout.isPending}
            />
            <Input
              placeholder="0x..."
              value={payoutTo}
              onChange={(e) => { setPayoutTo(e.target.value); setPayoutConfirming(false); }}
              className="font-mono text-sm max-w-sm"
              disabled={treasuryPayout.isPending}
            />
          </div>

          {/* Confirm flow */}
          {payoutConfirming ? (
            <div className="space-y-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
              <div className="flex items-start gap-2 text-sm text-amber-600">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  You are about to send <strong>{payoutAmount} {payoutToken}</strong> to{" "}
                  <span className="font-mono">{payoutTo.slice(0, 10)}…{payoutTo.slice(-6)}</span>. Confirm?
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleTreasuryPayout}
                  disabled={treasuryPayout.isPending}
                  className="gap-2"
                >
                  {treasuryPayout.isPending ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <SendHorizonal className="h-3.5 w-3.5" />
                  )}
                  {treasuryPayout.isPending ? "Sending…" : "Confirm Send"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPayoutConfirming(false)}
                  disabled={treasuryPayout.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPayoutConfirming(true)}
              disabled={!payoutAmount || !payoutTo || treasuryPayout.isPending}
              className="gap-2"
            >
              <SendHorizonal className="h-3.5 w-3.5" />
              Send {payoutToken}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Section 4: Contract Info & Ownership */}
      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-muted-foreground">
            <Landmark className="h-4 w-4 text-emerald-400" />
            Contract Info & Ownership
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Contract Address */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Contract Address</p>
              {isLoading && !data ? (
                <Skeleton className="h-5 w-52 bg-muted" />
              ) : data?.contract?.address ? (
                <a
                  href={`${explorerBase}/address/${data.contract.address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 font-mono text-sm text-primary/80 hover:text-primary"
                >
                  {data.contract.address.slice(0, 10)}...{data.contract.address.slice(-8)}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <span className="font-mono text-sm text-muted-foreground">—</span>
              )}
            </div>

            <div>
              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Current Owner</p>
              {isLoading && !data ? (
                <Skeleton className="h-5 w-52 bg-muted" />
              ) : data?.contract?.owner ? (
                <span className="font-mono text-sm">
                  {data.contract.owner.slice(0, 10)}...{data.contract.owner.slice(-8)}
                </span>
              ) : (
                <span className="font-mono text-sm text-muted-foreground">—</span>
              )}
            </div>

            <div>
              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Chain</p>
              {isLoading && !data ? (
                <Skeleton className="h-5 w-24 bg-muted" />
              ) : (
                <span className="font-mono text-sm">{data?.chain?.name ?? "—"}</span>
              )}
            </div>

            <div>
              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Chain ID</p>
              {isLoading && !data ? (
                <Skeleton className="h-5 w-16 bg-muted" />
              ) : (
                <span className="font-mono text-sm">{data?.chain?.id ?? "—"}</span>
              )}
            </div>
          </div>

          {/* Verification Badge */}
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-mono text-muted-foreground uppercase">Verification</p>
            {data?.contract?.isVerified ? (
              <Badge variant="outline" className="text-[9px] border-emerald-500/50 text-emerald-600 font-mono uppercase">
                Verified
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[9px] border-amber-500/50 text-amber-600 font-mono uppercase">
                Unverified
              </Badge>
            )}
          </div>

          {/* Transfer Ownership */}
          <div className="space-y-3 border-t border-divider/60 pt-4">
            <div>
              <p className="text-xs font-mono font-bold uppercase tracking-wider mb-1 text-destructive/80">
                Transfer Ownership
              </p>
              <p className="text-xs text-muted-foreground">
                Permanently transfers contract ownership to a new address. This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="0x..."
                value={newOwnerInput}
                onChange={(e) => setNewOwnerInput(e.target.value)}
                className="font-mono text-sm max-w-sm"
                disabled={transferOwnership.isPending}
              />
              <Button
                variant="destructive"
                size="sm"
                onClick={handleTransferOwnership}
                disabled={
                  !newOwnerInput ||
                  transferOwnership.isPending ||
                  newOwnerInput === data?.contract?.owner
                }
                className="gap-2 shrink-0"
              >
                {transferOwnership.isPending ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ArrowRightLeft className="h-3.5 w-3.5" />
                )}
                {transferOwnership.isPending ? "Transferring…" : "Transfer"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
